import { useMemo } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  BaseForm,
  FormInput,
  FormSelect,
  FormTag,
  FormTextarea,
} from "components/BaseForm";
import ACL_RELATIONSHIPS from "acl-relationships";
import EmailHtmlFrame from "components/email/EmailHtmlFrame";
import tagConstants from "constants/Tag";
import { decodeHtml } from "helpers/emailHelper";
import useAuthorized from "hooks/useAuthorized";
import { invalidateEmailFooterTableQuery } from "hooks/useEmailFooterTable";
import usePageTitle from "hooks/usePageTitle";
import EmailFooterService from "services/EmailFooters";
import EmailTemplateService from "services/EmailTemplate";

const MODEL_TYPE = tagConstants.taggableModels.email_footer;

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  html: z.string().min(1, "Custom HTML is required"),
  template_id: z.number().nullable(),
  tags: z.array(z.any()),
});

const getTemplateOptions = async () => {
  const templates = await EmailTemplateService.getEmailTemplatesForFilters();
  return templates.map(template => ({
    key: template.id,
    value: template.id,
    text: template.name,
  }));
};

/**
 * Renders the footer HTML inside the chosen template, so the user sees it
 * framed the way a recipient would. `template_id` drives the frame only and is
 * never saved with the footer.
 *
 * The preview goes in an iframe because email templates carry their own
 * `<style>` blocks. Rendered into the application document those rules are
 * global and restyle the app around the preview.
 */
const FooterPreview = () => {
  const templateId = useWatch({ name: "template_id" });
  const html = useWatch({ name: "html" });

  const { data: templateHtml = "" } = useQuery({
    queryKey: ["email-template-html", templateId],
    queryFn: async () => {
      const template = await EmailTemplateService.getEmailTemplate(templateId);
      return decodeHtml(template.html);
    },
    enabled: !!templateId,
  });

  const previewHtml = useMemo(() => {
    const before = templateHtml.indexOf("</body>");
    return (
      templateHtml.substring(0, before) +
      (html ?? "") +
      templateHtml.substring(before)
    );
  }, [templateHtml, html]);

  return (
    <EmailHtmlFrame
      html={previewHtml}
      title="Email footer preview"
      autoHeight
      style={{ minHeight: 120 }}
    />
  );
};

const EmailFooter = ({ mode }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isEdit = mode === "edit";
  const cloneId = searchParams.get("clone");
  const isClone = !isEdit && !!cloneId;
  const sourceId = isEdit ? id : cloneId;

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.emailFooters.edit
      : ACL_RELATIONSHIPS.emailFooters.create,
  ]);

  const {
    data: footerData,
    isSuccess: footerLoaded,
    isError: footerError,
    isFetchedAfterMount,
  } = useQuery({
    queryKey: ["email-footer", sourceId],
    queryFn: () => EmailFooterService.getEmailFooter(sourceId),
    enabled: !!sourceId,
    retry: false,
  });

  const { data: templateOptions = [] } = useQuery({
    queryKey: ["email-template-options"],
    queryFn: getTemplateOptions,
  });

  const defaultValues = useMemo(
    () => ({
      // A clone starts unnamed so the user is forced to pick a distinct name.
      name: isClone ? "" : (footerData?.name ?? ""),
      html: footerData ? decodeHtml(footerData.html) : "",
      template_id: null,
      tags: footerData?.tags ?? [],
    }),
    [footerData, isClone]
  );

  const pageTitle = isEdit
    ? "Edit Email Footer"
    : isClone
      ? "Clone Email Footer"
      : "New Email Footer";

  usePageTitle(isEdit ? footerData?.name : pageTitle);

  const handleSubmit = async ({ name, html, tags }) => {
    if (isEdit) {
      await EmailFooterService.editEmailFooter(id, name, html, tags);
    } else {
      await EmailFooterService.createEmailFooter(name, html, tags);
    }
  };

  const handleSuccess = async () => {
    await invalidateEmailFooterTableQuery();
    toast.success(isEdit ? "Email footer updated" : "Email footer created");
    navigate("/settings/email_footers");
  };

  const handleCancel = () => navigate("/settings/email_footers");

  if (!canWrite) {
    return <Navigate to="/settings/email_footers" replace />;
  }

  if (footerError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <p>{"Couldn't load this email footer. Please try again."}</p>
        <Link to="/settings/email_footers">Back to Email Footers</Link>
      </div>
    );
  }

  // isFetchedAfterMount, not just isSuccess: a cached entry resolves
  // synchronously as successful, and react-hook-form latches defaultValues at
  // the form's first render. Mounting on that payload would strand the form on
  // pre-save values, and saving would write them back over a newer edit.
  if (sourceId && !(footerLoaded && isFetchedAfterMount)) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center tw:font-sans tw:text-(--color-text-primary)">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        <BaseForm
          enablePageTitle
          pageTitle={pageTitle}
          backLink={{
            to: "/settings/email_footers",
            label: "Back to Email Footers",
          }}
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          submitButtonText="Save"
        >
          <Card>
            <CardHeader>
              <CardTitle>Footer</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="tw:flex tw:flex-col tw:gap-5">
                <FormInput name="name" label="Name" required />
                <FormTextarea
                  name="html"
                  label="Custom HTML"
                  required
                  rows={6}
                />
                <FormTag name="tags" modelType={MODEL_TYPE} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="tw:flex tw:flex-col tw:gap-5">
                <FormSelect
                  name="template_id"
                  label="Choose a Preview Template"
                  options={templateOptions}
                  clearable
                />
                <FooterPreview />
              </div>
            </CardContent>
          </Card>
        </BaseForm>
      </div>
    </div>
  );
};

export default EmailFooter;
